import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MFATypes } from '@flashbacktech/flashbackclient/api';
import { useApi } from './ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { useAuth } from './AuthContext';

interface MFAMethod {
  type: MFATypes.MFAType;
  name: string;
  description: string;
  isAvailable: boolean;
  isConfigured: boolean;
  isEnabled: boolean;
}

interface MFAStatusResponse {
  success: boolean;
  data: {
    isEnabled: boolean;
    isRequired: boolean;
    isEnforced: boolean;
    enabledMethods: MFATypes.MFAType[];
    primaryMethod?: MFATypes.MFAType;
  };
  error?: string;
}

interface MFAState {
  status: MFAStatusResponse['data'] | null;
  methods: MFAMethod[];
  isSettingUp: boolean;
  setupMethod: MFATypes.MFAType | null;
  setupData: any;
  isVerifying: boolean;
  verificationMethod: MFATypes.MFAType | null;
  isLoading: boolean;
  error: string | null;
}

interface MFAContextType {
  mfaState: MFAState;
  fetchMFAStatus: () => Promise<void>;
  fetchAvailableMethods: () => Promise<void>;
  setupMFAMethod: (
    mfaType: MFATypes.MFAType,
    email?: string
  ) => Promise<MFATypes.MFASetupResponse | null>;
  verifyMFASetup: (mfaType: MFATypes.MFAType, code?: string, credential?: any) => Promise<boolean>;
  enableMFAMethod: (mfaType: MFATypes.MFAType) => Promise<void>;
  disableMFAMethod: (mfaType: MFATypes.MFAType) => Promise<void>;
  setPrimaryMethod: (mfaType: MFATypes.MFAType) => Promise<void>;
  verifyMFA: (mfaType: MFATypes.MFAType, code?: string, credential?: any) => Promise<boolean>;
  verifyMFALogin: (
    request: MFATypes.MFAVerificationRequest
  ) => Promise<MFATypes.MFAVerificationLoginResponse>;
  clearError: () => void;
  resetSetupState: () => void;
}

const MFAContext = createContext<MFAContextType | undefined>(undefined);

export const useMFA = () => {
  const context = useContext(MFAContext);
  if (!context) {
    throw new Error('useMFA must be used within an MFAProvider');
  }
  return context;
};

interface MFAProviderProps {
  children: ReactNode;
}

export const MFAProvider: React.FC<MFAProviderProps> = ({ children }) => {
  const { apiClient } = useApi();
  const { authState } = useAuth();
  const withTokenRefresh = useWithTokenRefresh();

  const [mfaState, setMfaState] = useState<MFAState>({
    status: null,
    methods: [],
    isLoading: false,
    error: null,
    isSettingUp: false,
    setupMethod: null,
    setupData: null,
    isVerifying: false,
    verificationMethod: null,
  });

  const [hasInitialized, setHasInitialized] = useState(false);

  const clearError = () => {
    setMfaState((prev) => ({ ...prev, error: null }));
  };

  const resetSetupState = () => {
    setMfaState((prev) => ({
      ...prev,
      isSettingUp: false,
      setupMethod: null,
      setupData: null,
      isVerifying: false,
      verificationMethod: null,
    }));
  };

  const fetchMFAStatus = React.useCallback(async () => {
    if (!authState?.token) {
      return;
    }

    // Ensure the token is set in the API client
    apiClient.setAuthToken(authState.token);

    setMfaState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = (await withTokenRefresh(() => apiClient.getMFAStatus())) as any;
      if (response.success && response.data) {
        setMfaState((prev) => ({ ...prev, status: response.data }));
      } else {
        setMfaState((prev) => ({ ...prev, error: response.error || 'Failed to fetch MFA status' }));
      }
    } catch (error) {
      setMfaState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to fetch MFA status',
      }));
    } finally {
      setMfaState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [authState?.token, apiClient, withTokenRefresh]); // Added apiClient and withTokenRefresh dependencies

  const fetchAvailableMethods = React.useCallback(async () => {
    if (!authState?.token) {
      return;
    }

    // Ensure the token is set in the API client
    apiClient.setAuthToken(authState.token);

    setMfaState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = (await withTokenRefresh(() => apiClient.getAvailableMethods())) as any;
      if (response.success && response.data) {
        setMfaState((prev) => ({ ...prev, methods: response.data }));
      } else {
        setMfaState((prev) => ({
          ...prev,
          error: response.error || 'Failed to fetch MFA methods',
        }));
      }
    } catch (error) {
      setMfaState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to fetch MFA methods',
      }));
    } finally {
      setMfaState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [authState?.token, apiClient, withTokenRefresh]); // Added apiClient and withTokenRefresh dependencies

  const setupMFAMethod = async (
    mfaType: MFATypes.MFAType,
    email?: string
  ): Promise<MFATypes.MFASetupResponse | null> => {
    setMfaState((prev) => ({
      ...prev,
      isSettingUp: true,
      setupMethod: mfaType,
      error: null,
    }));

    try {
      // Ensure the token is set in the API client
      if (authState?.token) {
        apiClient.setAuthToken(authState.token);
      }

      const request: any = { mfaType };
      if (email) request.email = email;

      // Try different request formats that the backend might expect
      const alternativeRequests = [{ mfaType }, { type: mfaType }, { method: mfaType }];

      // Try to find the correct method name
      let response: any;
      try {
        response = (await withTokenRefresh(() => apiClient.setupMFA(request))) as any;
      } catch (apiError: any) {
        // Log the full error object to see what we're dealing with

        // Try to extract more details from the error response
        if (apiError?.response) {
          try {
            const errorResponse = await apiError.response.text();
          } catch (error) { /* Ignore errors */ }
        }

        // Try alternative method names that might exist
        if (
          'setupMFAMethod' in apiClient &&
          typeof (apiClient as any).setupMFAMethod === 'function'
        ) {
          response = (await withTokenRefresh(() =>
            (apiClient as any).setupMFAMethod(request)
          )) as any;
        } else if (
          'createMFAMethod' in apiClient &&
          typeof (apiClient as any).createMFAMethod === 'function'
        ) {
          response = (await withTokenRefresh(() =>
            (apiClient as any).createMFAMethod(request)
          )) as any;
        } else if (
          'initializeMFA' in apiClient &&
          typeof (apiClient as any).initializeMFA === 'function'
        ) {
          response = (await withTokenRefresh(() =>
            (apiClient as any).initializeMFA(request)
          )) as any;
        } else {
          // Try alternative request formats
          for (const altRequest of alternativeRequests) {
            try {
              response = (await withTokenRefresh(() =>
                apiClient.setupMFA(altRequest as any)
              )) as any;
              if (response && response.success) {
                break;
              }
            } catch (error) { /* Ignore errors */ }
          }

          if (!response || !response.success) {
            throw apiError;
          }
        }
      }

      // If we reach here, we have a response but it might not be successful
      if (!response) {
        return null;
      }

      // Minimal logging: show only the original challenge received
      if (response?.data?.challenge) { /* Empty */ }

      if (response.success && response.data) {
        setMfaState((prev) => ({
          ...prev,
          setupData: response.data,
        }));

        // Return the response so the caller can access it immediately
        return response;
      } else {
        setMfaState((prev) => ({
          ...prev,
          error: response.error || 'Failed to setup MFA method',
        }));

        // Return the failed response
        return response;
      }
    } catch (error) {
      setMfaState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to setup MFA method',
      }));

      // Return null on error
      return null;
    } finally {
      setMfaState((prev) => ({ ...prev, isSettingUp: false }));
    }

    // Final fallback return
    return null;
  };

  const verifyMFASetup = async (mfaType: MFATypes.MFAType, code?: string, credential?: any): Promise<boolean> => {
    setMfaState((prev) => ({
      ...prev,
      isVerifying: true,
      verificationMethod: mfaType,
      error: null,
    }));

    try {
      // Ensure the token is set in the API client
      if (authState?.token) {
        apiClient.setAuthToken(authState.token);
      }

      let response: any;

      if (mfaType === MFATypes.MFAType.PASSKEY && credential) {
        // Handle passkey completion
        const challenge = (credential as any).challenge || mfaState.setupData?.challenge;
        const cleanCredential = { ...credential };
        delete (cleanCredential as any).challenge; // Remove challenge from credential object

        // Minimal logging: show only the challenge being sent back
        if (challenge) { /* Empty */ }

        const passkeyRequest = { credential: cleanCredential, challenge: challenge };

        response = (await withTokenRefresh(() =>
          apiClient.completePasskeyRegistration(passkeyRequest)
        )) as any;
      } else {
        // Handle other MFA types
        const request: any = { mfaType };
        if (code) request.code = code;

        response = (await withTokenRefresh(() => apiClient.verifyMFASetup(request))) as any;
      }

      if (response.success) {
        // Clear setup data first
        resetSetupState();

        // Refresh MFA status and methods
        await Promise.all([fetchMFAStatus(), fetchAvailableMethods()]);
        return true;
      } else {
        setMfaState((prev) => ({
          ...prev,
          error: response.error || 'Failed to verify MFA setup',
        }));
        return false;
      }
    } catch (error) {
      setMfaState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to verify MFA setup',
      }));
      return false;
    } finally {
      setMfaState((prev) => ({ ...prev, isVerifying: false }));
    }
  };

  const enableMFAMethod = async (mfaType: MFATypes.MFAType) => {
    setMfaState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = (await withTokenRefresh(() => apiClient.enableMFA({ mfaType }))) as any;
      if (response.success) {
        await Promise.all([fetchMFAStatus(), fetchAvailableMethods()]);
      } else {
        setMfaState((prev) => ({
          ...prev,
          error: response.error || 'Failed to enable MFA method',
        }));
      }
    } catch (error) {
      setMfaState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to enable MFA method',
      }));
    } finally {
      setMfaState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const disableMFAMethod = async (mfaType: MFATypes.MFAType) => {
    setMfaState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = (await withTokenRefresh(() => apiClient.disableMFA({ mfaType }))) as any;
      if (response.success) {
        await Promise.all([fetchMFAStatus(), fetchAvailableMethods()]);
      } else {
        setMfaState((prev) => ({
          ...prev,
          error: response.error || 'Failed to disable MFA method',
        }));
      }
    } catch (error) {
      setMfaState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to disable MFA method',
      }));
    } finally {
      setMfaState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const setPrimaryMethod = async (mfaType: MFATypes.MFAType) => {
    setMfaState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = (await withTokenRefresh(() => apiClient.setPrimaryMFA({ mfaType }))) as any;
      if (response.success) {
        await fetchMFAStatus();
      } else {
        setMfaState((prev) => ({
          ...prev,
          error: response.error || 'Failed to set primary MFA method',
        }));
      }
    } catch (error) {
      setMfaState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to set primary MFA method',
      }));
    } finally {
      setMfaState((prev) => ({ ...prev, isLoading: false }));
    }
  };

  const verifyMFA = async (mfaType: MFATypes.MFAType, code?: string, credential?: any) => {
    setMfaState((prev) => ({
      ...prev,
      isVerifying: true,
      verificationMethod: mfaType,
      error: null,
    }));

    try {
      const request: any = { mfaType };
      if (code) request.code = code;
      if (credential) request.credential = credential;

      // For now, we'll use a direct fetch since this method might not exist in the client library
      const response = (await withTokenRefresh(() => apiClient.verifyMFALogin(request))) as any;

      if (response.success) {
        // MFA verified successfully
        setMfaState((prev) => ({ ...prev, isVerifying: false }));
        return true;
      } else {
        setMfaState((prev) => ({
          ...prev,
          error: response.message || 'MFA verification failed',
        }));
        return false;
      }
    } catch (error) {
      setMfaState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'MFA verification failed',
      }));
      return false;
    } finally {
      setMfaState((prev) => ({ ...prev, isVerifying: false }));
    }
  };

  const verifyMFALogin = async (
    request: MFATypes.MFAVerificationRequest
  ): Promise<MFATypes.MFAVerificationLoginResponse> => {
    setMfaState((prev) => ({
      ...prev,
      isVerifying: true,
      verificationMethod: request.mfaType,
      error: null,
    }));

    try {
      // Ensure the token is set in the API client
      if (authState?.token) {
        apiClient.setAuthToken(authState.token);
      }

      // Call the new MFA verification API directly without token refresh
      // MFA verification should use the current valid token
      const response = (await apiClient.verifyMFALogin(request)) as any;

      if (response.success) {
        // MFA verification successful - update state to indicate completion
        setMfaState((prev) => ({
          ...prev,
          isVerifying: false,
          // Clear MFA requirement since verification is complete
          status: null,
        }));
        return response;
      } else {
        // Don't throw error, return failed response so component can handle it
        setMfaState((prev) => ({
          ...prev,
          error: response.error || 'MFA verification failed',
        }));
        return response;
      }
    } catch (error) {
      // Handle unexpected errors (network issues, etc.)
      let errorMessage = 'MFA verification failed';

      // Check if it's a 401 error (wrong code) and show user-friendly message
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        errorMessage = 'Authentication code incorrect';
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setMfaState((prev) => ({
        ...prev,
        error: errorMessage,
      }));

      // Return a failed response instead of throwing
      return {
        success: false,
        message: errorMessage,
        fullAccessToken: '',
        refreshToken: '',
      };
    } finally {
      setMfaState((prev) => ({ ...prev, isVerifying: false }));
    }
  };

  // Initialize MFA data when component mounts and token is available
  useEffect(() => {
    if (authState?.token && !hasInitialized && typeof withTokenRefresh === 'function') {
      setHasInitialized(true);

      // Fetch MFA data immediately
      fetchMFAStatus();
      fetchAvailableMethods();
    } else if (!authState?.token) {
      // Reset initialization when token is lost
      setHasInitialized(false);
    } else { /* Empty */ }
    // Note: fetchMFAStatus and fetchAvailableMethods are stable callbacks (useCallback with stable deps)
    // They don't need to be in dependencies to avoid re-initialization
  }, [authState?.token, hasInitialized, withTokenRefresh, fetchMFAStatus, fetchAvailableMethods]);

  const value: MFAContextType = {
    mfaState,
    fetchMFAStatus,
    fetchAvailableMethods,
    setupMFAMethod,
    verifyMFASetup,
    enableMFAMethod,
    disableMFAMethod,
    setPrimaryMethod,
    verifyMFA,
    verifyMFALogin,
    clearError,
    resetSetupState,
  };

  return <MFAContext.Provider value={value}>{children}</MFAContext.Provider>;
};
